export const INTROSPECTION_QUERY = `
query IntrospectionQuery {
  __schema {
    queryType { name }
    mutationType { name }
    subscriptionType { name }
    types { ...FullType }
  }
}
fragment FullType on __Type {
  kind
  name
  description
  fields(includeDeprecated: true) {
    name
    description
    args { ...InputValue }
    type { ...TypeRef }
    isDeprecated
    deprecationReason
  }
  inputFields { ...InputValue }
  interfaces { ...TypeRef }
  enumValues(includeDeprecated: true) { name description isDeprecated deprecationReason }
  possibleTypes { ...TypeRef }
}
fragment InputValue on __InputValue {
  name
  description
  type { ...TypeRef }
  defaultValue
}
fragment TypeRef on __Type {
  kind
  name
  ofType {
    kind
    name
    ofType {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
          ofType {
            kind
            name
            ofType { kind name }
          }
        }
      }
    }
  }
}
`;

export interface GraphQLTypeRef {
  kind: string;
  name: string | null;
  ofType: GraphQLTypeRef | null;
}

export interface GraphQLArg {
  name: string;
  description: string | null;
  type: GraphQLTypeRef;
}

export interface GraphQLField {
  name: string;
  description: string | null;
  type: GraphQLTypeRef;
  args: GraphQLArg[];
  isDeprecated: boolean;
}

export interface GraphQLTypeInfo {
  kind: string;
  name: string;
  description: string | null;
  fields: GraphQLField[] | null;
}

export interface GraphQLSchemaInfo {
  queryType: string | null;
  mutationType: string | null;
  subscriptionType: string | null;
  types: GraphQLTypeInfo[];
}

export const formatTypeRef = (typeRef: GraphQLTypeRef | null): string => {
  if (!typeRef) return 'Unknown';
  if (typeRef.kind === 'NON_NULL') return `${formatTypeRef(typeRef.ofType)}!`;
  if (typeRef.kind === 'LIST') return `[${formatTypeRef(typeRef.ofType)}]`;
  return typeRef.name ?? 'Unknown';
};

/**
 * Runs the standard GraphQL introspection query directly from the browser
 * against the target endpoint — unlike the main send flow, which always
 * executes server-side to sidestep CORS, this is a best-effort utility for
 * populating the schema explorer. Endpoints with restrictive CORS will
 * fail with a clear network error rather than silently returning nothing.
 */
export const fetchGraphQLSchema = async (
  url: string,
  headers: Record<string, string>,
): Promise<GraphQLSchemaInfo> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ query: INTROSPECTION_QUERY, operationName: 'IntrospectionQuery' }),
  });

  if (!response.ok) {
    throw new Error(`Introspection request failed: ${response.status} ${response.statusText}`);
  }

  const json = (await response.json()) as {
    data?: {
      __schema?: {
        queryType?: { name: string };
        mutationType?: { name: string };
        subscriptionType?: { name: string };
        types?: GraphQLTypeInfo[];
      };
    };
    errors?: { message: string }[];
  };

  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0]?.message ?? 'The server returned GraphQL errors');
  }

  const schema = json.data?.__schema;
  if (!schema) {
    throw new Error('The response did not contain a valid schema');
  }

  return {
    queryType: schema.queryType?.name ?? null,
    mutationType: schema.mutationType?.name ?? null,
    subscriptionType: schema.subscriptionType?.name ?? null,
    types: schema.types ?? [],
  };
};
